// Ziele des Workflows (Runde 10): Erreicht ein Lead eines davon, endet sein Lauf sofort („Ziel erreicht“), die weiteren Schritte
// laufen nicht mehr. Mehrere Ziele gelten als „oder“. Eine Zeile je Ziel: Art ▾ und, je nach Art, die Angaben dazu.
import type { Bedingung } from "../api/workflows";
import { Auswahl } from "../components/Auswahl";
import { Icon } from "../components/Icon";
import { MenueKnopf, type MenuePunkt } from "../components/Menue";
import { useMeta } from "../daten/meta";
import { Bedingungen } from "./Bedingungen";
import { ANRUF_ERGEBNISSE, ZIELE, neuesZiel, zielInfo, zielProblem } from "./teil2Hilfen";
import type { Ziel, ZielArt } from "./teil2Api";

interface Props {
  ziele: Ziel[];
  onChange: (z: Ziel[]) => void;
  nurLesen?: boolean;
}

function ZielZeile({ ziel, nummer, nurLesen, onChange, onEntfernen }: { ziel: Ziel; nummer: number; nurLesen: boolean; onChange: (z: Ziel) => void; onEntfernen: () => void }) {
  const { meta } = useMeta();
  const c = ziel.config as { nach?: string[]; richtung?: string; disposition?: string[]; min_dauer_s?: number | null; type_id?: string | null; bedingungen?: Bedingung[] };
  const set = (teil: Record<string, unknown>) => onChange({ ...ziel, config: { ...ziel.config, ...teil } });
  const problem = zielProblem(ziel);
  const statuswahl = (meta?.lead_statuses ?? []).map((s) => ({ wert: s.id, label: s.label, farbe: s.color }));
  const typen = (meta?.custom_activity_types ?? []).filter((t) => !t.is_archived || t.id === c.type_id);
  return (
    <li className="wf-ziel" data-ziel={ziel.id}>
      <span className="wf-ziel__nummer" aria-hidden="true">
        {nummer}
      </span>
      <div className="wf-ziel__inhalt">
        <div className="wf-ziel__kopf">
          <div className="wf-ziel__art">
            <Auswahl
              optionen={ZIELE.map((z) => ({ wert: z.art, label: z.label, unter: z.text }))}
              wert={ziel.art}
              deaktiviert={nurLesen}
              breite={380}
              titel="Art des Ziels"
              onWahl={(w) => w && w !== ziel.art && onChange({ ...neuesZiel(w as ZielArt, []), id: ziel.id })}
            />
          </div>
          {problem ? (
            <span className="wf-schritt__warnung" title={problem}>
              <Icon name="warnung" groesse={14} />
              <span className="wf-schritt__warnung-text">{problem}</span>
            </span>
          ) : null}
          {!nurLesen ? (
            <button type="button" className="symbolknopf symbolknopf--klein wf-ziel__weg" title="Ziel entfernen" aria-label="Ziel entfernen" onClick={onEntfernen}>
              <Icon name="papierkorb" groesse={13} />
            </button>
          ) : null}
        </div>

        {ziel.art === "status" ? (
          <div className="feld wf-feld">
            <span className="feld__label">Wechselt in einen dieser Status</span>
            <Auswahl optionen={statuswahl} wert={c.nach ?? []} mehrfach platzhalter="Status wählen …" deaktiviert={nurLesen} onWahl={(w) => set({ nach: w })} />
          </div>
        ) : null}

        {ziel.art === "anruf" ? (
          <div className="wf-form__zeile">
            <div className="feld wf-feld">
              <span className="feld__label">Richtung</span>
              <div className="segment" role="group" aria-label="Richtung des Anrufs">
                {(
                  [
                    ["beide", "Beide"],
                    ["inbound", "Eingehend"],
                    ["outbound", "Ausgehend"],
                  ] as const
                ).map(([wert, label]) => (
                  <button key={wert} type="button" disabled={nurLesen} className={`segment__knopf${(c.richtung ?? "beide") === wert ? " segment__knopf--an" : ""}`} onClick={() => set({ richtung: wert })}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="feld wf-feld">
              <span className="feld__label">Ergebnis (leer = jedes)</span>
              <Auswahl optionen={ANRUF_ERGEBNISSE.map((e) => ({ wert: e.wert, label: e.label }))} wert={c.disposition ?? []} mehrfach platzhalter="Jedes Ergebnis" deaktiviert={nurLesen} onWahl={(w) => set({ disposition: w })} />
            </div>
            <div className="feld wf-feld">
              <span className="feld__label">Mindestens</span>
              <span className="wf-inline">
                <input
                  className="eingabe eingabe--schmal"
                  type="number"
                  min={0}
                  max={86400}
                  value={c.min_dauer_s ?? ""}
                  placeholder="0"
                  disabled={nurLesen}
                  onChange={(e) => set({ min_dauer_s: e.target.value === "" ? null : Math.max(0, Number(e.target.value) || 0) })}
                  aria-label="Mindestdauer in Sekunden"
                />
                <span className="leise">Sekunden</span>
              </span>
            </div>
          </div>
        ) : null}

        {ziel.art === "custom_activity" ? (
          <>
            <div className="feld wf-feld">
              <span className="feld__label">Typ der Custom Activity</span>
              <Auswahl
                optionen={typen.map((t) => ({ wert: t.id, label: t.name, unter: t.is_archived ? "archiviert" : null }))}
                wert={c.type_id ?? null}
                platzhalter="Typ wählen …"
                deaktiviert={nurLesen}
                onWahl={(w) => set({ type_id: w, bedingungen: [] })}
              />
            </div>
            {c.type_id ? (
              <div className="feld wf-feld wf-feld--breit">
                <span className="feld__label">Nur wenn (optional)</span>
                <Bedingungen quelle={{ aktivitaetTyp: c.type_id }} bedingungen={c.bedingungen ?? []} onChange={(b) => set({ bedingungen: b })} leerText="Jede veröffentlichte Aktivität dieses Typs zählt." />
              </div>
            ) : null}
          </>
        ) : null}

        {ziel.art === "bedingung" ? (
          <div className="feld wf-feld wf-feld--breit">
            <span className="feld__label">Der Lead erfüllt</span>
            <Bedingungen bedingungen={c.bedingungen ?? []} onChange={(b) => set({ bedingungen: b })} leerText="Noch keine Bedingung." />
          </div>
        ) : null}

        <p className="leise wf-ziel__text">{erklaerung(ziel)}</p>
      </div>
    </li>
  );
}

function erklaerung(z: Ziel): string {
  switch (z.art) {
    case "status":
      return "Zählt, wenn der Statuswechsel nach dem Einschreiben stattfindet, egal ob von Hand, per Massenbearbeitung oder durch einen anderen Workflow.";
    case "mail_eingang":
      return "Zählt jede E-Mail des Leads, die nach dem Einschreiben im Verlauf des Leads eingeht.";
    case "sms_eingang":
      return "Zählt jede SMS des Leads, die nach dem Einschreiben eingeht.";
    case "anruf":
      return "Zählt Anrufe nach dem Einschreiben, die zu Richtung, Ergebnis und Mindestdauer passen.";
    case "custom_activity":
      return "Zählt veröffentlichte Aktivitäten nach dem Einschreiben. Entwürfe zählen nicht.";
    case "bedingung":
      return "Das ist ein Zustand: Erfüllt der Lead die Bedingungen schon beim Einschreiben, endet der Lauf vor dem ersten Schritt.";
  }
}

export function ZieleEditor({ ziele, onChange, nurLesen = false }: Props) {
  const punkte: MenuePunkt[] = ZIELE.map((z) => ({
    label: z.label,
    icon: "erledigt",
    hinweis: z.text,
    rechts: <span className="leise wf-menue-text">{z.text}</span>,
    onClick: () => onChange([...ziele, neuesZiel(z.art, ziele)]),
  }));
  return (
    <div className="wf-form">
      {ziele.length === 0 ? <p className="leise wf-leer">Kein Ziel. Der Lauf endet erst, wenn alle Schritte durch sind.</p> : null}
      {ziele.length > 0 ? (
        <p className="leise wf-form__text">Der Lauf eines Leads endet, sobald er eines dieser Ziele erreicht. Die Schritte danach laufen nicht mehr. Im Bericht zählt das als „Ziel erreicht“.</p>
      ) : null}
      <ol className="wf-ziele">
        {ziele.map((z, i) => (
          <ZielZeile
            key={z.id}
            ziel={z}
            nummer={i + 1}
            nurLesen={nurLesen}
            onChange={(neu) => onChange(ziele.map((x) => (x.id === z.id ? neu : x)))}
            onEntfernen={() => onChange(ziele.filter((x) => x.id !== z.id))}
          />
        ))}
      </ol>
      {!nurLesen && ziele.length < 10 ? (
        <div className="wf-schritte__neu">
          <MenueKnopf knopfKlasse="knopf" titel="Ziel hinzufügen" punkte={punkte} breite={420}>
            <Icon name="plus" groesse={14} />
            Ziel hinzufügen
          </MenueKnopf>
        </div>
      ) : null}
    </div>
  );
}

export { zielInfo };
